import { useCallback, useEffect, useState } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useLocation } from 'react-router';
import { LocalStorageApi } from '~/utils/storage';
import { APP_DOWNLOAD_MODAL_SHOWN } from '~/utils/storage/contants';
import { useAppSelector } from '~/redux/hooks';
import { EntitlementFeatures } from '~/redux/api/routes/subscriptions/constants';
import { useGetAutologinUniversalLinkMutation } from '~/redux/api/routes/loginApi';
import { RoutePaths } from '~/routes/constants';

const APP_DOWNLOAD_FLAG = 'web-app-download-banner';
const BANNER_CLOSED_SESSION_KEY = 'app_download_banner_closed';
const PLAYER_ROUTES = ['/training'];
const MODAL_EXCLUDED_ROUTES = ['/login'];
// Subscription management (incl. cancellation) exists only on web; Support sends
// users straight here, so pushing them to the app would be a dead end.
const APP_PROMPT_EXCLUDED_ROUTES = ['/profile/account-management'];
const FALLBACK_APP_URL = 'https://yogio.onelink.me/f0wK/bvtkgeh2';

const isMobileOrTablet = (): boolean => {
  if ('userAgentData' in navigator) {
    return (navigator as Navigator & { userAgentData: { mobile: boolean } })
      .userAgentData.mobile;
  }
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export const useAppDownload = () => {
  const isFeatureEnabled = useFeatureIsOn(APP_DOWNLOAD_FLAG);
  const location = useLocation();
  const userEntitlements = useAppSelector((state) => state.entitlements.data);
  const entitlementsLoaded = useAppSelector(
    (state) => state.entitlements.isLoaded
  );
  const hasAiCoach = userEntitlements.some(
    (e) =>
      e.feature_id === EntitlementFeatures.ai_coach ||
      e.feature_id === EntitlementFeatures.ai_coach_male
  );

  const isPlayerRoute = PLAYER_ROUTES.some((r) =>
    location.pathname.startsWith(r)
  );
  const isModalExcludedRoute = MODAL_EXCLUDED_ROUTES.some((r) =>
    location.pathname.startsWith(r)
  );
  // The Support link goes through /autologin first, which loads entitlements
  // before redirecting; check its target too, or the prompt flashes there.
  const promptTargetPath = location.pathname.startsWith(RoutePaths.AUTOLOGIN)
    ? (new URLSearchParams(location.search).get('redirect') ?? '')
    : location.pathname;
  const isAppPromptExcludedRoute = APP_PROMPT_EXCLUDED_ROUTES.some((r) =>
    promptTargetPath.startsWith(r)
  );

  const isMobile = typeof window !== 'undefined' ? isMobileOrTablet() : false;

  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const [getAutologinUniversalLink, { data: autologinLinkData }] =
    useGetAutologinUniversalLinkMutation();

  useEffect(() => {
    if (!isFeatureEnabled || !isMobile) return;
    if (isAppPromptExcludedRoute) {
      setShowModal(false);
      setShowBanner(false);
      return;
    }
    if (!entitlementsLoaded && !isModalExcludedRoute) return;

    const modalShown = !!LocalStorageApi?.getItem(APP_DOWNLOAD_MODAL_SHOWN);
    const bannerClosed = !!sessionStorage.getItem(BANNER_CLOSED_SESSION_KEY);

    if (
      !modalShown &&
      !isModalExcludedRoute &&
      !hasAiCoach &&
      entitlementsLoaded
    ) {
      setShowModal(true);
      setShowBanner(false);
    } else if (!bannerClosed) {
      setShowModal(false);
      setShowBanner(!isPlayerRoute);
    } else {
      setShowModal(false);
      setShowBanner(false);
    }
  }, [
    isFeatureEnabled,
    isMobile,
    isPlayerRoute,
    isModalExcludedRoute,
    isAppPromptExcludedRoute,
    hasAiCoach,
    entitlementsLoaded,
  ]);

  // Issue a Chottu universal link with an embedded non-invalidating autologin
  // token as soon as banner/modal is about to be shown. The mobile app picks it
  // up via ChottuLinkService -> AutoLoginServiceImp.
  useEffect(() => {
    if (!showBanner && !showModal) return;
    if (autologinLinkData?.url) return;
    void getAutologinUniversalLink();
  }, [
    showBanner,
    showModal,
    autologinLinkData?.url,
    getAutologinUniversalLink,
  ]);

  const appUrl = autologinLinkData?.url ?? FALLBACK_APP_URL;

  const dismissModal = useCallback(() => {
    LocalStorageApi?.setItem(APP_DOWNLOAD_MODAL_SHOWN, true);
    setShowModal(false);
  }, []);

  const continueInBrowser = useCallback(() => {
    LocalStorageApi?.setItem(APP_DOWNLOAD_MODAL_SHOWN, true);
    setShowModal(false);
    setShowBanner(true);
  }, []);

  const closeBanner = useCallback(() => {
    sessionStorage.setItem(BANNER_CLOSED_SESSION_KEY, '1');
    setShowBanner(false);
  }, []);

  return {
    showModal,
    showBanner,
    dismissModal,
    continueInBrowser,
    closeBanner,
    appUrl,
  };
};
